import { CONTACT_INFO } from "@/data/siteData";
import { COMPANY_INFO } from "@/data/legalData";

// Shared by the React <Seo> component and scripts/seo-prerender.mjs (build time)
export const SITE_URL = "https://outsource-point.com";
export const SITE_NAME = "Outsource Point";

const DEFAULT_IMAGE = {
  url: `${SITE_URL}/og-image.jpg`,
  width: "1200",
  height: "630",
  alt: "Outsource Point — аутсорсинг кол център и управление на чат канали",
};

const ORG_ID = `${SITE_URL}/#organization`;

const ORGANIZATION_JSON_LD = {
  "@context": "https://schema.org",
  "@type": "Organization",
  "@id": ORG_ID,
  name: SITE_NAME,
  legalName: COMPANY_INFO.name,
  alternateName: "Аутсорс Пойнт",
  url: `${SITE_URL}/`,
  logo: `${SITE_URL}/favicon-512x512.png`,
  image: DEFAULT_IMAGE.url,
  description:
    "Българска аутсорсинг компания за кол център услуги, входящи и изходящи кампании и управление на чат канали.",
  foundingDate: "2016",
  email: CONTACT_INFO.email,
  telephone: CONTACT_INFO.phone,
  vatID: COMPANY_INFO.vat,
  taxID: COMPANY_INFO.eik,
  address: {
    "@type": "PostalAddress",
    streetAddress: "ул. Антим I, №1, Партер",
    addressLocality: "Враца",
    postalCode: "3000",
    addressCountry: "BG",
  },
  contactPoint: {
    "@type": "ContactPoint",
    contactType: "customer service",
    telephone: CONTACT_INFO.phone,
    email: CONTACT_INFO.email,
    availableLanguage: ["bg", "en"],
  },
  areaServed: "BG",
  knowsLanguage: ["bg", "en"],
  sameAs: [CONTACT_INFO.facebook, CONTACT_INFO.linkedin],
};

const WEBSITE_JSON_LD = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  "@id": `${SITE_URL}/#website`,
  name: SITE_NAME,
  url: `${SITE_URL}/`,
  inLanguage: "bg-BG",
  publisher: { "@id": ORG_ID },
};

// Title (short page name) + meta description for every static route
export const PAGE_SEO = {
  "/": {
    title: "Кол център услуги и управление на чат канали",
    navLabel: "Начало",
    description:
      "Outsource Point — българска аутсорсинг компания за кол център услуги, входящи и изходящи кампании и управление на чат канали на български и английски език.",
    jsonLd: [ORGANIZATION_JSON_LD, WEBSITE_JSON_LD],
  },
  "/za-nas": {
    title: "За нас",
    description:
      "Запознайте се с Outsource Point — българска аутсорсинг компания, основана през 2016 г., специализирана в кол център услуги и комуникационни решения.",
  },
  "/ekip": {
    title: "Екип",
    description:
      "Запознайте се с екипа на Outsource Point — професионалисти с дългогодишен опит в обслужването на клиенти и управлението на кол център кампании.",
  },
  "/uslugi": {
    title: "Услуги",
    description:
      "Пълен спектър от кол център услуги — входящи и изходящи кампании, 24/7 hotline, телемаркетинг, проучвания и управление на чат канали (WhatsApp, Viber, Messenger).",
  },
  "/ceni": {
    title: "Цени",
    description:
      "Прозрачни и конкурентни цени за управление на чат канали и кол център услуги. Вижте месечните планове и поискайте персонализирана оферта.",
  },
  "/blog": {
    title: "Блог",
    description:
      "Полезни статии за кол център услуги, управление на чат канали, телемаркетинг и обслужване на клиенти от експертите на Outsource Point.",
  },
  "/kontakti": {
    title: "Контакти",
    description:
      "Свържете се с Outsource Point — телефон, имейл и адрес в гр. Враца. Изпратете запитване и ще се свържем с вас възможно най-скоро.",
  },
  "/zapazi-chas": {
    title: "Запазете час",
    description:
      "Запазете час за безплатна консултация с Outsource Point. Попълнете формата и ще се свържем с вас за потвърждение на удобно време.",
  },
  "/politika-za-poveritelnost": {
    title: "Политика за поверителност",
    description:
      "Политика за поверителност на АУТСОРС ПОЙНТ ГРУП — какви лични данни събираме, за какви цели, на какво основание и какви са вашите права.",
  },
  "/biskvitki": {
    title: "Политика за бисквитки",
    description:
      "Политика за бисквитки на Outsource Point — необходими, аналитични и маркетингови бисквитки, доставчици, срокове и как да оттеглите съгласието си.",
  },
  "/obshti-usloviya": {
    title: "Общи условия",
    description:
      "Общи условия за ползване на уебсайта на Outsource Point (АУТСОРС ПОЙНТ ГРУП).",
  },
};

export const getBlogPostSeo = (post) => ({
  path: `/blog/${post.slug}`,
  title: post.title,
  description: post.metaDescription,
  image: post.image,
  type: "article",
  publishedTime: post.date,
  jsonLd: [
    {
      "@context": "https://schema.org",
      "@type": "BlogPosting",
      headline: post.title,
      description: post.metaDescription,
      image: post.image,
      datePublished: post.date,
      dateModified: post.date,
      articleSection: post.category,
      inLanguage: "bg-BG",
      author: { "@id": ORG_ID, "@type": "Organization", name: SITE_NAME },
      publisher: {
        "@id": ORG_ID,
        "@type": "Organization",
        name: SITE_NAME,
        logo: { "@type": "ImageObject", url: `${SITE_URL}/favicon-512x512.png` },
      },
      mainEntityOfPage: { "@type": "WebPage", "@id": `${SITE_URL}/blog/${post.slug}` },
    },
  ],
});

const breadcrumbJsonLd = (path, name) => {
  const items = [{ name: "Начало", url: `${SITE_URL}/` }];
  if (path.startsWith("/blog/")) items.push({ name: "Блог", url: `${SITE_URL}/blog` });
  items.push({ name, url: `${SITE_URL}${path}` });
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((it, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: it.name,
      item: it.url,
    })),
  };
};

const absoluteUrl = (u) => (u.startsWith("http") ? u : `${SITE_URL}${u}`);

// Returns everything that belongs in <head> for a page
export const buildSeo = ({
  path = "/",
  title,
  description,
  image,
  type = "website",
  publishedTime,
  noindex = false,
  jsonLd,
} = {}) => {
  const page = PAGE_SEO[path] || {};
  const name = title || page.title || SITE_NAME;
  const fullTitle = name.includes(SITE_NAME) ? name : `${name} | ${SITE_NAME}`;
  const desc = description || page.description || "";
  const url = `${SITE_URL}${path}`;
  const img = image ? { url: absoluteUrl(image), alt: name } : DEFAULT_IMAGE;

  const meta = [
    ["name", "description", desc],
    ["name", "robots", noindex ? "noindex, follow" : "index, follow, max-image-preview:large, max-snippet:-1"],
    ["property", "og:type", type],
    ["property", "og:site_name", SITE_NAME],
    ["property", "og:locale", "bg_BG"],
    ["property", "og:title", fullTitle],
    ["property", "og:description", desc],
    ["property", "og:url", url],
    ["property", "og:image", img.url],
    ["property", "og:image:secure_url", img.url],
    ["property", "og:image:width", img.width],
    ["property", "og:image:height", img.height],
    ["property", "og:image:alt", img.alt],
    ["property", "article:published_time", publishedTime],
    ["name", "twitter:card", "summary_large_image"],
    ["name", "twitter:title", fullTitle],
    ["name", "twitter:description", desc],
    ["name", "twitter:image", img.url],
    ["name", "twitter:image:alt", img.alt],
  ]
    .filter(([, , content]) => content)
    .map(([attr, key, content]) => ({ attr, key, content }));

  const ld = [...(jsonLd || page.jsonLd || [])];
  if (!noindex && path !== "/") ld.push(breadcrumbJsonLd(path, name));

  return { name, title: fullTitle, description: desc, meta, canonical: noindex ? null : url, jsonLd: ld };
};
